'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ChatInput,
  ChatInputHandles,
} from '@/components/thread/chat-input/chat-input';
import { SiteHeader } from '@/components/thread/thread-site-header';
import { useSidebar } from '@/components/ui/sidebar';
import { useThreadAgent } from '@/hooks/use-thread-agent';
import { useThreadInitialization } from '@/hooks/use-thread-initialization';
import { useThreadMessageSender } from '@/hooks/use-thread-message-sender';
import { useProjectNameSync } from '@/hooks/use-project-name-sync';
import { cn, createDenoiseThrottle } from '@/lib/utils';
import { PRE_START_TEXTS, FILE_TYPE_ICON_MAP } from '@/lib/config';
import { useFeedbackByMessage } from '@/hooks/use-feedback-by-message';

import {
  dispatchFeedbackModalClose,
  buildGroupMessages,
  getThreadStatusFromAgentRun,
} from '@/lib/thread';
import useDmToolCalls from '@/hooks/deep-mining/use-tool-calls';
import { DmLoadingBtn } from '@/components/thread/dm-loading-btn';
import { useImagePreloader } from '@/hooks/use-image-preloader';
import { EMPTY_FILE_CARDS } from '@/components/thread/dm-tool-views/file-empty';
import { DmWorkSpace } from '@/components/thread/dm-work-space';
import useThreadSeo from '@/hooks/deep-mining/use-thread-seo';
import useThreadTemplate from '@/hooks/deep-mining/use-thread-template';
import useThreadLocation from '@/hooks/deep-mining/use-thread-location';
import useSendCheck from '@/hooks/use-send-check';
import useThreadAuditStatus from '@/hooks/use-thread-audit-status';
import useModalMethod from '@/hooks/use-modal-method';
import { useStores } from '@/store/hooks/useStore';
import { useAutoScrollToBottom } from '@/hooks/auto-scroll/useAutoScrollToBottom';
import ThreadSkeleton from '@/components/thread/thread-skeleton';
import ErrorPage from '@/components/error-page';
import ThreadSplit from '@/components/thread/thread-split';
import ThreadMessageList from '@/components/thread/thread-message-list';
import { getAgentRuns, saveMessageExtraMetadata } from '@/services/thread';
import { AgentRun } from '@/lib/api';
import { bindRef } from '@/hooks/auto-scroll';
import { useMcpuiActions } from '@/hooks/use-mcpui-actions';
import { useDepsChange } from '@/hooks/use-deps-change';
import { useI18n } from '@/hooks/use-i18n';
import { useAsyncTask } from '@/hooks/deep-mining/use-async-task';
import { useGlobalConfig } from '@/contexts/global-config-context';
import { getDefaultMcpUiUserCancelPrompt } from '@/constants/agent';
import { asyncTaskEditStore } from '@/store/modules/async-task-edit-store';
import ConfirmModal from '@/components/deep-mining/confirm-modal';

function ThreadPage({ params }: { params: Promise<{ threadId: string }> }) {
  const { t, i18n } = useI18n(['agent', 'common']);
  const modal = useModalMethod();

  const stores = useStores();
  const {
    threadInfo,
    setThreadStatus,
    updateThreadInfo,
    topMessageIds,
    messageMap,
    streamingTextContent,
    pendingMessages,
    setStagedMessages,
  } = stores.threadStore;
  const { streamingToolCalls, toolCalls } = stores.threadToolStore;
  const {
    project,
    projectName,
    sandboxFileSystemPrefix,
    setProjectName,
    sandboxStatus,
  } = stores.threadProjectStore;
  const { switchToViewsTab } = stores.threadWorkspaceStore;
  const { checkUnsavedChanges } = stores.asyncTaskEditStore;
  const { permissions } = stores.userStore;
  const [enableFeedback, enablePromptTemplate] = useMemo(
    () => [
      permissions?.includes('feedback'),
      permissions?.includes('prompt_template'),
    ],
    [permissions],
  );
  const { globalConfig } = useGlobalConfig();

  // 统一的未保存更改确认弹窗状态
  const { pendingConfirmation, confirm, cancel } = asyncTaskEditStore();
  const userCancelPromptTemplate = useMemo(() => {
    return (
      globalConfig?.mcp_ui_user_cancel_prompt?.value ||
      getDefaultMcpUiUserCancelPrompt()
    );
  }, [globalConfig]);
  const scrollBehaviorRef = useRef<'smooth' | 'instant'>('instant');

  const unwrappedParams = React.use(params);
  const threadId = unwrappedParams.threadId;

  // 使用 useFeedbackByMessage hook 获取按消息ID分组的反馈数据
  const { feedbackByMessageId, batchInsertFeedbacks, batchDeleteFeedbacks } =
    useFeedbackByMessage({ enable: enableFeedback, token: threadId });

  const containerRef_ = useRef<HTMLDivElement>(null);
  const { ref: autoScrollRef, scrollToBottom: __scrollToBottom__ } =
    useAutoScrollToBottom([threadId], {
      autoScrollLockDefault: true,
    });

  const scrollToBottom = createDenoiseThrottle(
    __scrollToBottom__,
    50,
    'throttle',
  );

  // 使用 Agent 流式处理 hook
  const {
    agentStatus,
    setAgentStatus,
    rateAgentRunId,
    setAgentRunId,
    setRateAgentRunId,
    isRunning,
    isStopping,
    handleStopAgent,
  } = useThreadAgent({
    threadId,
    scrollToBottom,
  });

  const containerRef = bindRef(containerRef_, autoScrollRef);

  const forceScrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'instant') => {
      containerRef_.current?.scrollTo({
        top: containerRef_.current?.scrollHeight ?? 0,
        behavior,
      });
    },
    [],
  );
  useEffect(() => {
    if (!isRunning) return;
    scrollToBottom(scrollBehaviorRef.current);
  }, [
    topMessageIds,
    streamingTextContent,
    streamingToolCalls,
    messageMap,
    Object.values(messageMap)?.length,
  ]);

  useEffect(() => {
    if (!isRunning || !threadId) return;
    const timer = setInterval(() => {
      scrollToBottom(scrollBehaviorRef.current);
    }, 500);
    return () => clearInterval(timer);
  }, [isRunning, threadId]);

  useDepsChange(
    ([preIsRunning, curIsRunning]) => {
      // 判断Bool哈，不然undefined 也是假值
      if (preIsRunning && !curIsRunning) {
        // 在结束的时候也要滚一下 因为有一些样式会展示，这里延迟一下，等待样式渲染完毕
        setTimeout(() => {
          forceScrollToBottom('smooth');
        }, 100);
        // 兜底：部分组件渲染较慢（如评分卡片），再补一次确保到底
        setTimeout(() => {
          forceScrollToBottom('instant');
        }, 500);
      }
    },
    [isRunning],
  );

  // 敏感词审核失败
  const [noRemindAuditFailed, setNoRemindAuditFailed] = useState(false);

  const chatInputRef = useRef<ChatInputHandles>(null);

  const { state: leftSidebarState, setOpen: setLeftSidebarOpen } = useSidebar();
  const initialLayoutAppliedRef = useRef(false);

  useImagePreloader(EMPTY_FILE_CARDS.map((item) => item.icon));
  useImagePreloader(Object.values(FILE_TYPE_ICON_MAP), { loadOnIdle: true });

  useEffect(() => {
    // file_system_prefix可能会改变，在启动沙盒的时候需要更新thread的file_system_prefix
    if (sandboxFileSystemPrefix) {
      updateThreadInfo({
        metadata: {
          ...threadInfo?.metadata,
          file_system_prefix: sandboxFileSystemPrefix,
        },
      });
    }
  }, [sandboxFileSystemPrefix]);

  useEffect(() => {
    if (!initialLayoutAppliedRef.current) {
      setLeftSidebarOpen(false);
      initialLayoutAppliedRef.current = true;
    }
  }, [setLeftSidebarOpen]);

  // Update keyboard shortcut handlers to manage both panels
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // CMD+B for Left Sidebar
      if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
        event.preventDefault();
        // If left sidebar is expanded, collapse it
        if (leftSidebarState === 'expanded') {
          setLeftSidebarOpen(false);
        } else {
          // Otherwise expand the left sidebar and close the side panel
          setLeftSidebarOpen(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [leftSidebarState, setLeftSidebarOpen]);

  const checkAgentRuns = useCallback(async () => {
    try {
      console.log('[PAGE] Checking for active agent runs...');
      const agentRuns = await getAgentRuns(threadId);

      // agent run 是倒序的
      let lastRun: AgentRun | null = null;
      if (agentRuns.length) {
        lastRun = agentRuns[0];
        // 设置线程状态
        const threadStatus = getThreadStatusFromAgentRun(lastRun);
        if (threadStatus) {
          const [status, metadata] = threadStatus;
          setThreadStatus(status, metadata);
        }

        // 设置评分 agent run ID
        if (!(lastRun?.metadata?.score > 0)) {
          setRateAgentRunId(lastRun.id);
        }
      }
      return agentRuns;
    } catch (err) {
      console.error('[PAGE] get agent runs error', err);
    }
  }, []);

  // 异步任务的tool处理
  const {
    activeAsyncTask,
    checkLastTaskIsRunning,
    checkAsyncTaskBeforeSend,
    getToolCallsCb,
  } = useAsyncTask({
    checkAgentRuns,
    setAgentRunId,
    setAgentStatus,
  });

  useDmToolCalls({ topMessageIds, getToolCallsCb });

  // 使用数据初始化 hook
  const { loading, error } = useThreadInitialization({
    threadId,
    setAgentRunId,
    setRateAgentRunId,
    scrollToBottom: forceScrollToBottom,
    checkAgentRuns,
    checkLastTaskIsRunning,
  });

  // 使用消息发送 hook
  const { isSending, handleSubmitMessage } = useThreadMessageSender({
    threadId,
    setAgentRunId,
    scrollToBottom: forceScrollToBottom,
    chatInputRef,
  });

  // 监听，更新threadInfo
  useEffect(() => {
    const handleApiNotify = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { thread_id, event: _event, metadata } = customEvent?.detail;
      if (_event === 'thread_info' && thread_id === threadId) {
        console.log('接收到thread_info更新的event消息', metadata);
        updateThreadInfo({
          metadata: metadata,
        });
      }
    };

    // Add event listener
    window.addEventListener('api-notify', handleApiNotify as EventListener);
    // Cleanup
    return () => {
      window.removeEventListener(
        'api-notify',
        handleApiNotify as EventListener,
      );
    };
  }, [threadId]);

  useEffect(() => {
    setTimeout(() => {
      scrollBehaviorRef.current = 'smooth';
    }, 800);
  }, [threadId]);

  const { onBeforeSend: _onBeforeSend } = useSendCheck();
  const setAuditStatus = useCallback(
    (status: MISC.AuditStatus) => {
      updateThreadInfo({
        metadata: {
          audit_status: status,
        },
      });
    },
    [threadId],
  );
  useThreadAuditStatus({
    threadId,
    setAuditStatus,
  });

  // 项目名称同步
  useProjectNameSync({ setProjectName });

  useThreadSeo({ projectName, pageType: 'conv' });

  useThreadLocation();

  useThreadTemplate({ threadId });

  const isEmptyThread =
    !topMessageIds.length &&
    !streamingTextContent &&
    !streamingToolCalls &&
    agentStatus === 'idle';
  const enableCreateTemplate = useMemo(() => {
    if (loading || error || !topMessageIds.length) {
      return false;
    }
    const isTemplateThread = !!threadInfo?.metadata?.user_thread_template_id;

    return !isTemplateThread;
  }, [topMessageIds, loading, error, threadInfo]);
  // 修改位置符合
  const groupedMessages = (() => {
    if (isEmptyThread) {
      return [];
    }
    const possibleAddVirtualAssistantGroup =
      isRunning && !!streamingTextContent;
    const { groupedMessages } = buildGroupMessages({
      topMessageIds,
      messageMap: messageMap,
      possibleAddVirtualAssistantGroup,
    });
    return groupedMessages;
  })();
  // 检查是否所有用户输入都审核失败
  const allUserInputAuditFailed = groupedMessages.every(
    (group) => group.auditInfo?.failed,
  );

  // 检查是否有模型输出审核失败
  const hasModelOutputAuditFailed =
    threadInfo?.metadata?.audit_status === 'rejected';
  const threadAuditStatus: AUDIT.ThreadAuditStatus = hasModelOutputAuditFailed
    ? 'model_output_rejected'
    : allUserInputAuditFailed
      ? 'all_user_input_rejected'
      : 'normal';

  // 检查是否有敏感词审核失败
  const auditFailedCheck = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      if (threadAuditStatus !== 'model_output_rejected') {
        resolve(true);
        return;
      }

      if (noRemindAuditFailed) {
        resolve(true);
        return;
      }

      modal.confirmTip({
        key: 'audit-failed-warning',
        title: t('common:tip'),
        content: t('agent:page.auditFailedWarning'),
        okText: t('common:iKnow'),
        cancelText: t('common:noLongerRemind'),
        onCancel: () => {
          setNoRemindAuditFailed(true);
          resolve(true);
        },
        onOk: () => {
          resolve(true);
        },
      });
    });
  }, [noRemindAuditFailed, threadAuditStatus, t, modal]);

  // 是否有没有处理的 wait complete 的 mcpui tool
  const pendingMcpuiToolInfo = useMemo(() => {
    const pendingTools = toolCalls.filter((toolCall) => {
      const { metadata } = toolCall;
      // 检查是否有 wait_completion 且 mcpui_status 没有值
      return metadata?.wait_completion && !metadata?._extra?.mcpui_status;
    });
    const lastTool = pendingTools[pendingTools.length - 1];
    return {
      hasPending: pendingTools.length > 0,
      pendingTools,
      lastTool,
    };
  }, [toolCalls]);

  // 为最后一个 pending tool 创建 useMcpuiActions hook 实例
  const lastPendingToolActions = useMcpuiActions({
    threadId: pendingMcpuiToolInfo.lastTool?.threadId || '',
    toolMessageId: pendingMcpuiToolInfo.lastTool?.toolMessageId || '',
    toolName:
      pendingMcpuiToolInfo.lastTool?.name === 'functions'
        ? pendingMcpuiToolInfo.lastTool?.function_name
        : pendingMcpuiToolInfo.lastTool?.name,
  });

  // 检查是否有未完成的 MCP UI 交互操作
  const checkPendingMcpuiTool = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      if (!pendingMcpuiToolInfo.hasPending) {
        resolve(true);
        return;
      }

      modal.confirmTip({
        key: 'pending-mcpui-warning',
        title: t('common:confirmInterrupt'),
        content: t('agent:page.pendingMcpuiInterruptWarning'),
        okText: t('common:interrupt'),
        cancelText: t('common:cancel'),
        onCancel: () => {
          resolve(false);
        },
        onOk: async () => {
          const pendingTools = pendingMcpuiToolInfo.pendingTools;

          if (pendingTools.length === 0) {
            resolve(true);
            return;
          }

          // 只处理最后一个工具（取消方式3）
          const lastTool = pendingMcpuiToolInfo.lastTool;

          if (
            !lastTool?.threadId ||
            !lastTool?.toolMessageId ||
            !lastTool?.name
          ) {
            resolve(true);
            return;
          }

          // 使用组件顶层创建的 useMcpuiActions hook 实例
          const allowCustomCancel =
            lastTool.metadata?.allow_custom_cancel === true;

          const result = await lastPendingToolActions.handleHostCancel({
            allowCustomCancel,
            promptTemplateType: 'user_cancel',
            shouldSend: false,
          });
          // 存 stagedMessage 方便发送的时候消费
          const stagedMessage = {
            content: result.prompt,
            metadata: {
              is_hide: true,
            },
          };
          setStagedMessages([stagedMessage]);
          resolve(true);
        },
      });
    });
  }, [pendingMcpuiToolInfo, modal]);

  // 计算分享按钮的禁用状态和提示信息
  const shareDisabledInfo = useMemo(() => {
    const isRunning = agentStatus !== 'idle';
    const hasAsyncTask = !!activeAsyncTask;
    const isAuditRejected = threadAuditStatus === 'model_output_rejected';
    const isAuditAbnormal = threadAuditStatus !== 'normal';

    // 优先级: 敏感信息 > Agent运行中 > 异步任务 > 未完成的交互操作
    if (isAuditRejected) {
      return {
        disabled: true,
        message: t('thread:siteHeader.sensitiveInfoNoShare'),
      };
    }

    if (isRunning) {
      return {
        disabled: true,
        message: t('thread:siteHeader.agentRunning'),
      };
    }

    if (hasAsyncTask) {
      return {
        disabled: true,
        message: t('thread:siteHeader.asyncTaskPending'),
      };
    }

    if (pendingMcpuiToolInfo.hasPending) {
      return {
        disabled: true,
        message: t('thread:siteHeader.pendingInteraction'),
      };
    }

    if (isAuditAbnormal) {
      return {
        disabled: true,
        message: t('thread:siteHeader.auditAbnormal'),
      };
    }

    return {
      disabled: false,
      message: t('thread:siteHeader.shareThread'),
    };
  }, [
    agentStatus,
    activeAsyncTask,
    threadAuditStatus,
    pendingMcpuiToolInfo.hasPending,
  ]);

  const onBeforeSend = useCallback(
    async (selectedWorkers?: string[]) => {
      // 1. 检查是否有未保存的 AsyncTask 编辑
      const canProceedWithUnsavedChanges = await checkUnsavedChanges();
      if (!canProceedWithUnsavedChanges) {
        return false;
      }

      // 2. 基础发送检查
      const canSend = await _onBeforeSend();
      if (!canSend) {
        return false;
      }

      // 3. 检查是否有正在进行中的异步任务
      const checkAsyncTaskRes = await checkAsyncTaskBeforeSend();
      if (!checkAsyncTaskRes) {
        return false;
      }

      // 4. 检查是否有未完成的 MCP UI 交互操作
      const canContinueWithMcpui = await checkPendingMcpuiTool();
      if (!canContinueWithMcpui) {
        return false;
      }

      // 5. 敏感词审核检查，但不阻止发送，只是弹窗提示
      await auditFailedCheck();
      return true;
    },
    [
      _onBeforeSend,
      auditFailedCheck,
      checkAsyncTaskBeforeSend,
      checkPendingMcpuiTool,
    ],
  );

  // 这两个变量都是 loadData 结束之后设置的
  if (loading) {
    return <ThreadSkeleton showInput />;
  }

  // error 也是 loadData 的时候设置的
  if (error) {
    return (
      <ErrorPage
        message={error}
        defaultMessage={t('agent:page.fetchThreadFailed')}
        className="h-screen"
      />
    );
  }

  return (
    <div className="flex h-screen">
      <ThreadSplit>
        <div
          className={`flex flex-col overflow-hidden transition-all duration-200 ease-in-out w-[calc(100%/3-16px)]`}
        >
          <SiteHeader
            threadId={threadId}
            projectName={projectName}
            projectId={project?.id || ''}
            onProjectRenamed={setProjectName}
            isMobileView={false}
            threadMetadata={threadInfo?.metadata}
            enableShareCreateTemplate={false}
            shareDisabled={shareDisabledInfo.disabled}
            shareMessage={shareDisabledInfo.message}
          />

          <div
            ref={containerRef}
            className="flex-1 overflow-y-auto px-6 pt-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
          >
            <div className="mx-auto max-w-3xl">
              {isEmptyThread ? (
                <div className="flex h-full items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    {t('agent:page.emptyThreadPlaceholder')}
                  </div>
                </div>
              ) : (
                <ThreadMessageList
                  groupedMessages={groupedMessages}
                  handleSubmitMessage={handleSubmitMessage}
                  enableCreateTemplate={enableCreateTemplate}
                  agentStatus={agentStatus}
                  threadAuditStatus={threadAuditStatus}
                  feedbackByMessageId={feedbackByMessageId}
                  batchInsertFeedbacks={batchInsertFeedbacks}
                  batchDeleteFeedbacks={batchDeleteFeedbacks}
                  isRunning={isRunning}
                  rateAgentRunId={rateAgentRunId}
                  setRateAgentRunId={setRateAgentRunId}
                  scrollToBottom={forceScrollToBottom}
                  isStopping={isStopping}
                  onStopAgent={handleStopAgent}
                />
              )}
            </div>
          </div>
          <div
            className={cn(
              'bottom-0 z-10 bg-gradient-to-t from-background via-background/90 to-transparent px-6 transition-all duration-200 ease-in-out mt-4',
              leftSidebarState === 'expanded' ? 'left-[256px]' : 'left-[47px]',
              leftSidebarState === 'expanded'
                ? 'right-[calc((100%-256px)*2/3)]'
                : 'right-[calc((100%-47px)*2/3)]',
            )}
          >
            <div className={cn('mx-auto relative', 'max-w-3xl')}>
              {/* 
                这里加这个判断逻辑的原因是因为在发送消息成功之后 到 停止这个区间这个按钮会闪一下
                所以这里我们认为：只要是处于二次发送的阶段就不展示这个按钮
               */}
              {!pendingMessages[0]?.phase && (
                <div className="flex items-center justify-center gap-2 absolute top-0 -translate-y-1/1 left-1/2 -translate-x-1/2 w-full">
                  <div className="inline-flex items-center align-middle">
                    {sandboxStatus === 'CREATING' && (
                      <DmLoadingBtn text={PRE_START_TEXTS} />
                    )}
                    {sandboxStatus === 'RUNNING' &&
                      !(agentStatus === 'idle') && (
                        <DmLoadingBtn
                          text={t('agent:page.viewCurrentProgress')}
                          onClick={() => {
                            switchToViewsTab();
                            forceScrollToBottom('smooth');
                            dispatchFeedbackModalClose();
                          }}
                        />
                      )}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-center"></div>
              <ChatInput
                ref={chatInputRef}
                key={threadId}
                onSubmit={handleSubmitMessage}
                onBeforeSubmit={onBeforeSend}
                placeholder={
                  enablePromptTemplate
                    ? t('agent:page.chatInputPlaceholder')
                    : t('agent:page.chatInputPlaceholder2')
                }
                /**
                 * pendingMessages 说明：
                 * - stopping：二次发送触发的"停止中"，允许用户继续编辑并覆盖 pending
                 * - sending：停止完成后自动发送 pending 的阶段，允许用户添加 waiting
                 * - waiting：等待前一条发送成功后再触发暂停，允许用户覆盖 waiting
                 *
                 * 因此，只要有 pendingMessages，输入框都是可用的。
                 */
                loading={
                  isSending || (isStopping && pendingMessages.length === 0)
                }
                disabled={
                  isSending ||
                  // 无 pending 时：仍按运行/停止状态禁用输入
                  (pendingMessages.length === 0 &&
                    (isStopping ||
                      agentStatus === 'running' ||
                      agentStatus === 'connecting'))
                }
                /**
                 * 二次发送相关阶段：虽然 Agent 可能仍在运行，但从用户交互角度，
                 * 输入框应该表现为"正常可发送"状态（显示发送图标），因此此时传 false。
                 */
                isAgentRunning={
                  pendingMessages.length > 0
                    ? false
                    : agentStatus === 'running' || agentStatus === 'connecting'
                }
                onStopAgent={handleStopAgent}
                autoFocus={!loading}
                filePosition="outer"
                showSkillSelector={true}
              />
            </div>
          </div>
        </div>
        {/* TODO 传参改成直接访问 store */}
        <DmWorkSpace
          isLoading={loading}
          isStreaming={sandboxStatus === 'RUNNING' && !(agentStatus === 'idle')}
          project={project}
          threadId={threadId}
        />
      </ThreadSplit>

      {/* 统一的未保存更改确认弹窗 */}
      <ConfirmModal
        title={t('common:confirmLeaveTitle')}
        content={t('common:confirmLeaveContent')}
        isOpen={!!pendingConfirmation}
        onClose={cancel}
        onConfirm={confirm}
      />
    </div>
  );
}

export default ThreadPage;
